"use strict";
/**
 * Copyright © 2020 Johnson & Johnson
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPathValue = void 0;
var react_1 = __importStar(require("react"));
var core_1 = require("@bodiless/core");
var informed_1 = require("informed");
var path_1 = __importDefault(require("path"));
var BASE_PATH_FIELD_NAME = 'basePath';
var PAGE_URL_FIELD_NAME = 'pagePath';
var BASE_PATH_EMPTY_VALUE = '/';
var INPUT_FIELD_DEFAULT_CLASSES = 'bl-text-grey-900 bl-bg-grey-100 bl-text-xs bl-min-w-xl-grid-1 bl-my-grid-2 bl-p-grid-1';
var INPUT_FIELD_INLINE_CLASSES = INPUT_FIELD_DEFAULT_CLASSES.concat(' bl-inline');
var INPUT_FIELD_BLOCK_CLASSES = INPUT_FIELD_DEFAULT_CLASSES.concat(' bl-block bl-w-full');
var usePagePath = function () { return core_1.useNode().node.pagePath; };
var useBasePathField = function () {
    var basePath = usePagePath();
    var _a = informed_1.useField({
        field: BASE_PATH_FIELD_NAME,
        initialValue: basePath,
    }), fieldState = _a.fieldState, fieldApi = _a.fieldApi, ref = _a.ref, userProps = _a.userProps;
    var value = fieldState.value;
    var setValue = fieldApi.setValue;
    var onChange = userProps.onChange, onBlur = userProps.onBlur, rest = __rest(userProps, ["onChange", "onBlur"]);
    return __assign({ ref: ref,
        value: value,
        setValue: setValue,
        onChange: onChange }, rest);
};
var isEmptyValue = function (value) { return Boolean(value) === false; };
var validateEmptyField = function (value) { return (isEmptyValue(value)
    ? 'Field can not be empty'
    : undefined); };
var validatePageUrl = function (value) { return (typeof value === 'string' && !RegExp(/^[a-z0-9_/-]+$/i).test(value)
    ? 'No special characters or spaces allowed'
    : undefined); };
var validatePagePath = function (value) { return (typeof value === 'string' && !RegExp(/^[a-z0-9_-]+$/i).test(value)
    ? 'No special characters or spaces allowed'
    : undefined); };
var getPageUrlValidator = function (validate) { return react_1.useCallback(function (value, values) { return validateEmptyField(value)
    || validatePageUrl(value)
    || (validate && validate(value, values)); }, []); };
var getPagePathValidator = function (validate) { return react_1.useCallback(function (value, values) { return validateEmptyField(value)
    || validatePagePath(value)
    || (validate && validate(value, values)); }, []); };
var joinPath = function (path1, path2) { return path_1.default.join(path1, path2); };
var fieldValueToUrl = function (value) { return (typeof value === 'string'
    ? value || BASE_PATH_EMPTY_VALUE
    : BASE_PATH_EMPTY_VALUE); };
/**
 * informed custom field that provides ability to enter new page path
 * the field contains 2 inputs: base path and page path
 * it is recommended to use getPathValue function to merge these 2 inputs
 * and to get result page path after the form containing this field is submitted
 * @param props informed field props
 */
var NewPageURLField = function (props) {
    var _a = core_1.useMenuOptionUI(), ComponentFormLabel = _a.ComponentFormLabel, ComponentFormLink = _a.ComponentFormLink, ComponentFormWarning = _a.ComponentFormWarning;
    var _b = useBasePathField(), basePathValue = _b.value, setBasePathValue = _b.setValue, restBasePathProps = __rest(_b, ["value", "setValue"]);
    var isBasePathEmpty = isEmptyValue(basePathValue) || basePathValue === BASE_PATH_EMPTY_VALUE;
    var isFullUrl = isBasePathEmpty;
    var validate = props.validate, rest = __rest(props, ["validate"]);
    var _c = informed_1.useField(__assign({ field: PAGE_URL_FIELD_NAME, validate: isFullUrl ? getPageUrlValidator(validate) : getPagePathValidator(validate), placeholder: isFullUrl ? '/mypath/mypage' : 'my-page' }, rest)), fieldState = _c.fieldState, fieldApi = _c.fieldApi, render = _c.render, ref = _c.ref, userProps = _c.userProps;
    var value = fieldState.value;
    var setValue = fieldApi.setValue, setError = fieldApi.setError;
    var onChange = userProps.onChange, restUserProps = __rest(userProps, ["onChange"]);
    var fieldLabel = isFullUrl ? 'URL' : 'Page Path';
    var inputClasses = isFullUrl ? INPUT_FIELD_BLOCK_CLASSES : INPUT_FIELD_INLINE_CLASSES;
    return render(react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement(ComponentFormLabel, { htmlFor: "new-page-path" }, fieldLabel),
        !isFullUrl
            ? (react_1.default.createElement("span", { className: "mr-1" }, "" + basePathValue))
            : null,
        react_1.default.createElement("input", __assign({}, restBasePathProps, { type: "hidden", value: isBasePathEmpty ? BASE_PATH_EMPTY_VALUE : basePathValue })),
        react_1.default.createElement("input", __assign({ name: "new-page-path", className: inputClasses }, restUserProps, { ref: ref, value: isEmptyValue(value) ? '' : value, onChange: function (e) {
                setValue(e.target.value);
                if (onChange) {
                    onChange(e);
                }
            } })),
        !isBasePathEmpty
            && (react_1.default.createElement("div", { className: "bl-block" },
                react_1.default.createElement(ComponentFormLink, { onClick: function () {
                        setValue(joinPath(basePathValue, fieldValueToUrl(value)));
                        setBasePathValue(BASE_PATH_EMPTY_VALUE);
                        if (validatePageUrl(value) === undefined)
                            setError(undefined);
                    } }, "Edit"))),
        fieldState.error ? (react_1.default.createElement(ComponentFormWarning, null, fieldState.error)) : null));
};
/**
 * function that can be used to get new page path value
 * this function should usually be invoked after an informed form
 * containing NewPageURLField field is submitted
 * @param values informed form values
 * @returns new page path
 */
var getPathValue = function (values) {
    var _a = values, _b = BASE_PATH_FIELD_NAME, basePagePath = _a[_b], _c = PAGE_URL_FIELD_NAME, pageUrl = _a[_c];
    return joinPath(fieldValueToUrl(basePagePath), fieldValueToUrl(pageUrl));
};
exports.getPathValue = getPathValue;
exports.default = NewPageURLField;
//# sourceMappingURL=NewPageURLField.js.map